import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminSignIn, fetchAuthProviders } from "../lib/api";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import { ShieldCheck, Lock } from "lucide-react";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const expiredNoticeShown = useRef(false);

  useEffect(() => {
    fetchAuthProviders()
      .then(setProviders)
      .catch(() => setProviders({ email: true }));
  }, []);

  useEffect(() => {
    if (!location.state?.sessionExpired || expiredNoticeShown.current) return;
    expiredNoticeShown.current = true;
    toast.info("Votre session a expiré. Reconnectez-vous.");
    navigate("/admin/login", { replace: true, state: {} });
  }, [location.state?.sessionExpired, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!providers?.email) {
      toast.error("La connexion par email n'est pas activée sur cette instance.");
      return;
    }
    setLoading(true);
    try {
      await adminSignIn(email.trim(), password);
      toast.success("Accès admin validé");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  if (!providers) {
    return (
      <PageLayout title="Money Drop — Admin">
        <div className="md-card p-8 max-w-md mx-auto text-center text-white/70">Chargement…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Money Drop — Admin">
      <form onSubmit={submit} className="md-card p-6 sm:p-8 max-w-md mx-auto" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-md-electric/15 border-2 border-md-cyan/40 flex items-center justify-center">
            <ShieldCheck className="text-md-cyan" size={20} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">Salle de contrôle</div>
            <div className="text-lg font-bold text-white">Connexion Admin</div>
          </div>
        </div>

        {providers.email ? (
          <>
            <label className="block text-sm font-medium text-md-silver/80 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="md-input"
              placeholder="admin@example.com"
              data-testid="admin-email-input"
              required
            />

            <label className="block text-sm font-medium text-md-silver/80 mb-2 mt-4">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="md-input"
              placeholder="••••••••"
              data-testid="admin-password-input"
              required
            />

            <MdGlowButton type="submit" disabled={loading} wrapClassName="mt-0" data-testid="admin-login-button">
              {loading ? "Connexion..." : "Accéder au panneau"}
            </MdGlowButton>
          </>
        ) : (
          <p className="text-sm text-white/60">Aucune méthode de connexion admin n'est configurée.</p>
        )}

        <div className="mt-6 pt-4 border-t border-md-cyan/10 flex items-center justify-between text-xs text-white/45">
          <div className="flex items-center gap-2">
            <Lock size={12} /> Accès restreint
          </div>
          <a href="/" className="hover:text-md-cyan transition-colors">
            ← Retour
          </a>
        </div>
      </form>
    </PageLayout>
  );
};

export default AdminLogin;
